"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import type { NodeViewProps } from "@tiptap/react"
import { NodeViewWrapper } from "@tiptap/react"

const MIN_WIDTH_PCT = 10
const MAX_WIDTH_PCT = 100
/** Only snap to left/center/right when image center is within this % of 0, 50, or 100 */
const SNAP_THRESHOLD_PCT = 8

type ResizeHandle = "left" | "right"

/**
 * Image with scale and alignment applied at the image level:
 * - One container is sized (width %) and positioned (margin) so the img sits in the right place.
 * - The img fills that container (100%); resize handles sit on the container edges.
 * - Dragging the image horizontally repositions it; it sticks exactly where dropped (positionPct).
 * - Toolbar align (left/center/right) sets align and clears positionPct.
 */
export function ImageNodeView({
  node,
  updateAttributes,
  editor,
}: NodeViewProps) {
  const { src, alt, title, width, align, positionPct } = node.attrs as {
    src: string
    alt?: string
    title?: string
    width?: string
    align?: string
    positionPct?: number | null
  }

  const outerRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const [resizing, setResizing] = useState<ResizeHandle | null>(null)
  const [dragging, setDragging] = useState(false)
  const dragStartXRef = useRef<number>(0)
  const dragStartWrapperCenterXRef = useRef<number>(0)
  const resizeStartRef = useRef<{
    handle: ResizeHandle
    startX: number
    startWidthPx: number
    containerWidthPx: number
  } | null>(null)

  const currentWidthPct = width ? Number(width) : 100
  const clampedPct = Math.min(
    MAX_WIDTH_PCT,
    Math.max(MIN_WIDTH_PCT, isNaN(currentWidthPct) ? 100 : currentWidthPct)
  )

  const alignValue =
    align === "left" || align === "center" || align === "right" ? align : "left"

  // Continuous position (0–100): center of image as % of container. When set, image sticks there.
  const hasPositionPct =
    positionPct != null && !Number.isNaN(positionPct) && positionPct >= 0 && positionPct <= 100

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const start = resizeStartRef.current
      if (!start || !outerRef.current) return

      const { handle, startX, startWidthPx, containerWidthPx } = start
      const delta = handle === "left" ? startX - e.clientX : e.clientX - startX

      const newWidthPx = Math.max(0, startWidthPx + delta)
      const newPct = Math.round((newWidthPx / containerWidthPx) * 100)
      const clamped = Math.min(
        MAX_WIDTH_PCT,
        Math.max(MIN_WIDTH_PCT, newPct)
      )
      updateAttributes({ width: String(clamped) })
    },
    [updateAttributes]
  )

  const handleMouseUp = useCallback(() => {
    setResizing(null)
    resizeStartRef.current = null
  }, [])

  useEffect(() => {
    if (!resizing) return
    window.addEventListener("mousemove", handleMouseMove)
    window.addEventListener("mouseup", handleMouseUp)
    return () => {
      window.removeEventListener("mousemove", handleMouseMove)
      window.removeEventListener("mouseup", handleMouseUp)
    }
  }, [resizing, handleMouseMove, handleMouseUp])

  const handleResizeStart = useCallback(
    (e: React.MouseEvent, handle: ResizeHandle) => {
      e.preventDefault()
      e.stopPropagation()
      if (!editor.isEditable || !outerRef.current || !wrapperRef.current)
        return
      const outer = outerRef.current
      const wrapper = wrapperRef.current
      setResizing(handle)
      resizeStartRef.current = {
        handle,
        startX: e.clientX,
        startWidthPx: wrapper.offsetWidth,
        containerWidthPx: outer.offsetWidth,
      }
    },
    [editor.isEditable]
  )

  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      if (!editor.isEditable || resizing) return
      if (
        e.target instanceof HTMLElement &&
        e.target.closest(".tiptap-image-resize-handle")
      )
        return
      e.preventDefault()
      dragStartXRef.current = e.clientX
      const wrapper = wrapperRef.current
      if (wrapper) {
        const wr = wrapper.getBoundingClientRect()
        dragStartWrapperCenterXRef.current = wr.left + wr.width / 2
      }
      setDragging(true)
    },
    [editor.isEditable, resizing]
  )

  const handleDragMove = useCallback((e: MouseEvent) => {
    const wrapper = wrapperRef.current
    const outer = outerRef.current
    if (!wrapper || !outer) return
    const outerRect = outer.getBoundingClientRect()
    const outerWidth = outerRect.width
    if (outerWidth <= 0) return

    const rawDeltaX = e.clientX - dragStartXRef.current
    const currentCenterX =
      dragStartWrapperCenterXRef.current + rawDeltaX
    const positionPct =
      ((currentCenterX - outerRect.left) / outerWidth) * 100

    const distToLeft = Math.abs(positionPct - 0)
    const distToCenter = Math.abs(positionPct - 50)
    const distToRight = Math.abs(positionPct - 100)

    let snapDeltaX: number
    if (distToLeft <= SNAP_THRESHOLD_PCT) {
      snapDeltaX = outerRect.left - dragStartWrapperCenterXRef.current
    } else if (distToCenter <= SNAP_THRESHOLD_PCT) {
      snapDeltaX =
        outerRect.left + outerWidth / 2 - dragStartWrapperCenterXRef.current
    } else if (distToRight <= SNAP_THRESHOLD_PCT) {
      snapDeltaX =
        outerRect.left + outerWidth - dragStartWrapperCenterXRef.current
    } else {
      snapDeltaX = rawDeltaX
    }

    wrapper.style.transform = `translateX(${snapDeltaX}px)`
  }, [])

  const handleDragEnd = useCallback(() => {
    const outer = outerRef.current
    const wrapper = wrapperRef.current
    if (!outer || !wrapper) {
      setDragging(false)
      return
    }
    const outerRect = outer.getBoundingClientRect()
    const wrapperRect = wrapper.getBoundingClientRect()
    const wrapperCenterX = wrapperRect.left + wrapperRect.width / 2
    const outerWidth = outerRect.width
    if (outerWidth <= 0) {
      wrapper.style.transform = ""
      setDragging(false)
      return
    }

    const positionPctValue =
      ((wrapperCenterX - outerRect.left) / outerWidth) * 100
    const distToLeft = Math.abs(positionPctValue - 0)
    const distToCenter = Math.abs(positionPctValue - 50)
    const distToRight = Math.abs(positionPctValue - 100)

    if (distToLeft <= SNAP_THRESHOLD_PCT) {
      updateAttributes({ align: "left", positionPct: null })
    } else if (distToCenter <= SNAP_THRESHOLD_PCT) {
      updateAttributes({ align: "center", positionPct: null })
    } else if (distToRight <= SNAP_THRESHOLD_PCT) {
      updateAttributes({ align: "right", positionPct: null })
    } else {
      const clamped =
        Math.round(positionPctValue * 10) / 10
      updateAttributes({
        positionPct: Math.min(100, Math.max(0, clamped)),
      })
    }

    wrapper.style.transform = ""
    setDragging(false)
  }, [updateAttributes])

  useEffect(() => {
    if (!dragging) return
    window.addEventListener("mousemove", handleDragMove)
    window.addEventListener("mouseup", handleDragEnd)
    return () => {
      window.removeEventListener("mousemove", handleDragMove)
      window.removeEventListener("mouseup", handleDragEnd)
    }
  }, [dragging, handleDragMove, handleDragEnd])

  const showHandles = editor.isEditable
  const handles: ResizeHandle[] = ["left", "right"]
  const canDrag = editor.isEditable

  // Image-level sizing and position: width % and margin so image sticks where placed
  const marginLeftPct =
    hasPositionPct && positionPct != null
      ? Math.max(0, Math.min(100 - clampedPct, positionPct - clampedPct / 2))
      : null
  const containerStyle: React.CSSProperties = {
    width: `${clampedPct}%`,
    maxWidth: `${clampedPct}%`,
    ...(marginLeftPct != null
      ? { marginLeft: `${marginLeftPct}%`, marginRight: 0 }
      : {
          marginLeft: alignValue === "center" || alignValue === "right" ? "auto" : 0,
          marginRight: alignValue === "center" || alignValue === "left" ? "auto" : 0,
        }),
    cursor: canDrag ? (dragging ? "grabbing" : "grab") : undefined,
    userSelect: dragging ? "none" : undefined,
  }

  return (
    <NodeViewWrapper className="tiptap-image-align-wrapper-outer">
      <div ref={outerRef} className="tiptap-image-align-wrapper-outer-inner">
        <div
          ref={wrapperRef}
          className="tiptap-image-align-wrapper"
          data-align={alignValue}
          data-width={String(clampedPct)}
          style={containerStyle}
          onMouseDown={handleDragStart}
          role={canDrag ? "button" : undefined}
          aria-label={canDrag ? "Drag to reposition image" : undefined}
        >
          <div className="tiptap-image-align-wrapper-inner">
            {showHandles &&
              handles.map((handle) => (
                <div
                  key={handle}
                  className={`tiptap-image-resize-handle tiptap-image-resize-handle-${handle}`}
                  onMouseDown={(e) => handleResizeStart(e, handle)}
                  role="slider"
                  aria-label={`Resize image from ${handle}`}
                  tabIndex={-1}
                />
              ))}
            <img
              ref={imgRef}
              src={src ?? ""}
              alt={alt ?? ""}
              title={title ?? ""}
              draggable={false}
              style={{ width: "100%", maxWidth: "100%", display: "block" }}
            />
          </div>
        </div>
      </div>
    </NodeViewWrapper>
  )
}
